'use client'

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import React, { useEffect, useRef, useState } from 'react'

import AttributionCapture from './AttributionCapture'
import ClientPortal from './ClientPortal'
import RudderstackAnalyticsBootstrap from './RudderstackAnalyticsBootstrap'
import SessionStoreSync from './SessionStoreSync'
import SmsMarketingConfigBootstrap from './SmsMarketingConfigBootstrap'
import SmsSubscriptionStateBootstrap from './SmsSubscriptionStateBootstrap'

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queryClient] = useState(() => new QueryClient())
  const persisterRef = useRef<ReturnType<typeof createSyncStoragePersister> | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!persisterRef.current) {
      persisterRef.current = createSyncStoragePersister({
        storage: window.localStorage,
      })
    }

    const persister = persisterRef.current
    if (!persister) return

    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
    })

    return () => {
      unsubscribe()
    }
  }, [queryClient])

  return (
    <QueryClientProvider client={queryClient}>
      {/* Keep providers minimal so static HTML stays visible to no-JS crawlers. */}
      <SessionStoreSync />
      <AttributionCapture />
      <RudderstackAnalyticsBootstrap />
      <SmsMarketingConfigBootstrap />
      <SmsSubscriptionStateBootstrap />
      {children}
      <ClientPortal queryClient={queryClient} persisterRef={persisterRef} />
    </QueryClientProvider>
  )
}

export default AppProviders
